import { useTranslation } from 'react-i18next';
import { FileText, ChevronRight } from 'lucide-react';
import type { KBArticleListItem } from '@/services/api/knowledge-base';

interface KBArticleListProps {
  articles: KBArticleListItem[];
  isLoading: boolean;
  onArticleClick: (articleId: number) => void;
}

export default function KBArticleList({ articles, isLoading, onArticleClick }: KBArticleListProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="space-y-2 py-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-btn bg-base-100 border border-go-border p-3 animate-pulse">
            <div className="h-4 w-3/4 bg-base-300 rounded mb-2" />
            <div className="h-3 w-full bg-base-300 rounded mb-1" />
            <div className="h-3 w-2/3 bg-base-300 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-base-content/50">
        <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>{t('kb.articles.empty')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5 py-1">
      {articles.map((article) => (
        <button
          key={article.id}
          type="button"
          className="w-full text-left rounded-btn bg-base-100 border border-go-border/60 p-3 hover:border-primary/30 hover:bg-base-100/80 transition-all cursor-pointer group"
          onClick={() => onArticleClick(article.id)}
        >
          <div className="flex items-start gap-2.5">
            <div className="h-7 w-7 rounded-btn bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <FileText className="h-3.5 w-3.5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-medium leading-tight group-hover:text-primary transition-colors">
                {article.title}
              </h4>
              <p className="text-xs text-base-content/50 mt-1 line-clamp-2 leading-relaxed">
                {article.excerpt}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-base-content/20 group-hover:text-primary/60 shrink-0 mt-1 transition-colors" />
          </div>
        </button>
      ))}
    </div>
  );
}
